// ============================================================
// DocumentPreviewFrame.tsx — (batch 2) the ONE preview of a client document
// (settlement statement, bill, credit note, case summary): a SANDBOXED iframe
// (no script, no same-origin access — audit H3) that is revealed only once
// the document is complete, fonts included, so it appears in one go.
//
// Why it used to flash (colours, then text): sandbox="" gives the frame an
// opaque origin, so its requests for the bundled fonts (public/document-fonts)
// were cross-origin and CORS-blocked — the text sat invisible for the
// font-display: block period, then fell back to system fonts. Now:
//   1. the font URLs are swapped for data: URIs first (utils/documentFontFiles
//      inlineDocumentFonts — fetched once per file in the app's origin, cached),
//      so the frame needs no requests at all;
//   2. each new document loads in a HIDDEN frame and is shown at its load
//      event — Chromium holds a document's load event for the web fonts that
//      layout asked for (checked headless: every face "loaded" at load), so
//      the reveal is complete. Until then the previous document stays on
//      screen (double-buffered — an edit never blanks the preview), or, the
//      first time, a "Preparing preview…" placeholder at the same size;
//   3. a safety timeout reveals the frame anyway if load never comes.
// The parent never touches the frame's document (it can't — opaque origin).
// ============================================================
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { inlineDocumentFonts } from '../../utils/documentFontFiles'

// Reveal a frame whose load event hasn't come by then (never seen in practice).
export const PREVIEW_REVEAL_TIMEOUT_MS = 4000

interface Layer { id: number; html: string; shown: boolean }

const DocumentPreviewFrame: React.FC<{ title: string; html: string; className?: string }> = ({ title, html, className = 'w-full h-[70vh]' }) => {
  const [layers, setLayers] = useState<Layer[]>([])
  const nextId = useRef(0)

  // New HTML → inline its fonts → add it as a hidden layer on top. Pending
  // layers that never got shown are dropped (only the newest matters).
  useEffect(() => {
    let live = true
    void inlineDocumentFonts(html).catch(() => html).then((ready) => {
      if (!live) return
      const id = ++nextId.current
      setLayers((ls) => [...ls.filter((l) => l.shown).slice(-1), { id, html: ready, shown: false }])
    })
    return () => { live = false }
  }, [html])

  // Show a layer (its load event, or the safety timeout) and drop the ones under it.
  // (Audit fix batch 2, Low 12) If the shown layer had keyboard focus, the new
  // one takes it once revealed — focus never drops to the page when it swaps.
  const box = useRef<HTMLDivElement | null>(null)
  const focusNext = useRef<number | null>(null)
  const reveal = useCallback((id: number): void => {
    const active = document.activeElement
    if (active && active.tagName === 'IFRAME' && box.current?.contains(active)) focusNext.current = id
    setLayers((ls) => (ls.some((l) => l.id === id) ? ls.filter((l) => l.id >= id).map((l) => (l.id === id ? { ...l, shown: true } : l)) : ls))
  }, [])
  useEffect(() => {
    const id = focusNext.current
    if (id === null) return
    const el = box.current?.querySelector<HTMLIFrameElement>(`iframe[data-layer="${id}"]`)
    if (el && layers.some((l) => l.id === id && l.shown)) { focusNext.current = null; el.focus() }
  }, [layers])
  const pending = layers.find((l) => !l.shown)?.id
  useEffect(() => {
    if (pending === undefined) return
    const t = setTimeout(() => reveal(pending), PREVIEW_REVEAL_TIMEOUT_MS)
    return () => clearTimeout(t)
  }, [pending, reveal])

  const anyShown = layers.some((l) => l.shown)
  return (
    <div ref={box} className={`relative overflow-hidden ${className} rounded-[10px] border border-casevo-border bg-white`} aria-busy={!anyShown || pending !== undefined}>
      {!anyShown && (
        <div className="absolute inset-0 p-[28px] flex flex-col gap-[12px]" role="status" data-testid="document-preview-placeholder">
          <div className="h-[22px] w-[38%] rounded-[6px] bg-black/[0.06] animate-pulse" />
          <div className="h-[86px] w-full rounded-[10px] bg-black/[0.05] animate-pulse" />
          <div className="h-[12px] w-[72%] rounded-[4px] bg-black/[0.05] animate-pulse" />
          <div className="h-[12px] w-[64%] rounded-[4px] bg-black/[0.05] animate-pulse" />
          <p className="mt-[6px] text-[13px] text-black/60">Preparing preview…</p>
        </div>
      )}
      {layers.map((l) => (
        <iframe
          key={l.id}
          data-layer={l.id}
          title={title}
          sandbox=""
          srcDoc={l.html}
          onLoad={() => reveal(l.id)}
          // Hidden layers are invisible AND out of the tab order / a11y tree.
          aria-hidden={l.shown ? undefined : true}
          tabIndex={l.shown ? undefined : -1}
          style={{ visibility: l.shown ? 'visible' : 'hidden' }}
          className="absolute inset-0 w-full h-full border-0 bg-white"
        />
      ))}
    </div>
  )
}

export default DocumentPreviewFrame
